export async function fetchJSON<T>(url: string, init?: RequestInit): Promise<T> {
  // Ensure all calls go through the central authenticatedFetch logic so that
  // token-refresh and CSRF handling are unified throughout the app.
  const { authenticatedFetch, parseJsonResponse } = await import('./apiClient');
  const response = await authenticatedFetch(adjustUrl(url), init);
  return parseJsonResponse(response) as Promise<T>;
}

export async function fetchBlob(url: string, init?: RequestInit): Promise<Blob> {
  const { authenticatedFetch } = await import('./apiClient');
  const response = await authenticatedFetch(adjustUrl(url), init);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.blob();
}

/**
 * Minimal Axios-like facade so existing AtlasClient code (api.get / api.post / api.put)
 * can stay unchanged during the migration. Only the methods currently used by
 * the codebase are implemented (get, post & put). They return an object with the
 * signature `{ data: T }`, mirroring Axios's response shape.
 */
export const api = {
  async get<T>(url: string, config?: { params?: Record<string, any>; responseType?: string; headers?: Record<string, any> }): Promise<{ data: T }> {
    let finalUrl = withParams(adjustUrl(url), config?.params);
    if (config?.responseType === 'blob') {
      const data = (await fetchBlob(finalUrl, { headers: config?.headers })) as unknown as T;
      return { data };
    }
    const data = await fetchJSON<T>(finalUrl, { headers: config?.headers });
    return { data };
  },

  async post<T>(url: string, data?: any, config?: { headers?: Record<string, any>; responseType?: string }): Promise<{ data: T }> {
    const init: RequestInit = {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data ?? {}),
      headers: config?.headers,
    };

    // For FormData we must let the browser set the multipart boundary by not adding a Content-Type header.
    if (data instanceof FormData) {
      if (init.headers) {
        delete (init.headers as any)['Content-Type'];
      }
    }

    if (config?.responseType === 'blob') {
      const blob = (await fetchBlob(adjustUrl(url), init)) as unknown as T;
      return { data: blob };
    }
    const json = await fetchJSON<T>(adjustUrl(url), init);
    return { data: json };
  },

  async put<T>(url: string, data?: any, config?: { headers?: Record<string, any>; responseType?: string }): Promise<{ data: T }> {
    const init: RequestInit = {
      method: 'PUT',
      body: data instanceof FormData ? data : JSON.stringify(data ?? {}),
      headers: config?.headers,
    };

    // For FormData we must let the browser set the multipart boundary by not adding a Content-Type header.
    if (data instanceof FormData) {
      if (init.headers) {
        delete (init.headers as any)['Content-Type'];
      }
    }

    if (config?.responseType === 'blob') {
      const blob = (await fetchBlob(adjustUrl(url), init)) as unknown as T;
      return { data: blob };
    }
    const json = await fetchJSON<T>(adjustUrl(url), init);
    return { data: json };
  },

  async delete<T>(url: string, config?: { headers?: Record<string, any>; responseType?: string }): Promise<{ data: T }> {
    const init: RequestInit = {
      method: 'DELETE',
      headers: config?.headers,
    };

    if (config?.responseType === 'blob') {
      const blob = (await fetchBlob(adjustUrl(url), init)) as unknown as T;
      return { data: blob };
    }
    const json = await fetchJSON<T>(adjustUrl(url), init);
    return { data: json };
  },
};

// ---------------------------------------------------------------------------
// Helper utilities
// ---------------------------------------------------------------------------

function adjustUrl(url: string): string {
  // Preserve absolute URLs (http/https) and already-prefixed /api URLs
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return url;
  // Otherwise, prefix with /api to emulate the previous Axios baseURL
  if (url.startsWith('/')) return `/api${url}`;
  return `/api/${url}`;
}

function withParams(base: string, params?: Record<string, any>): string {
  if (!params || Object.keys(params).length === 0) return base;
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      usp.append(key, String(value));
    }
  });
  const glue = base.includes('?') ? '&' : '?';
  return `${base}${glue}${usp.toString()}`;
}
